import apiClient from "../../authentication/utils/core/axios";

const API_URL = '/groups';

const GroupAPI = {
  // Groups CRUD
  fetchGroups: async (skip = 0, limit = 10) => {
    const res = await apiClient.get(`${API_URL}/`, {
      params: { skip, limit }
    });
    if (res.status !== 200) throw new Error('Failed to fetch groups');
    return res.data;
  },

  fetchGroupById: async (groupId) => {
    const res = await apiClient.get(`${API_URL}/${groupId}`);
    if (res.status !== 200) throw new Error('Failed to fetch group');
    return res.data;
  },

  fetchGroupByName: async (name) => {
    const res = await apiClient.get(`${API_URL}/by-name/${name}`);
    if (res.status !== 200) throw new Error('Failed to fetch group by name');
    return res.data;
  },

  createGroup: async (data) => {
    const res = await apiClient.post(`${API_URL}/`, data);
    if (res.status < 200 || res.status >= 300) throw new Error('Failed to create group');
    return res.data;
  },

  updateGroup: async (groupId, data) => {
    const res = await apiClient.put(`${API_URL}/${groupId}`, data);
    if (res.status !== 200) throw new Error('Failed to update group');
    return res.data;
  },

  deleteGroup: async (groupId) => {
    const res = await apiClient.delete(`${API_URL}/${groupId}`);
    if (res.status !== 204) throw new Error('Failed to delete group');
    return groupId;
  },

  // Group Members
  fetchGroupMembers: async (groupId) => {
    const res = await apiClient.get(`${API_URL}/${groupId}/members`);
    if (res.status !== 200) throw new Error('Failed to fetch group members');
    return res.data;
  },

  addMemberToGroup: async (groupId, userId) => {
    const res = await apiClient.post(`${API_URL}/${groupId}/members/${userId}`);
    if (res.status !== 200) throw new Error('Failed to add member');
    return res.data;
  },

  removeMemberFromGroup: async (groupId, userId) => {
    const res = await apiClient.delete(`${API_URL}/${groupId}/members/${userId}`);
    if (res.status !== 204) throw new Error('Failed to remove member');
    return { groupId, userId };
  },

  joinGroup: async (groupId) => {
    const res = await apiClient.post(`${API_URL}/${groupId}/join`);
    if (res.status !== 200) throw new Error('Failed to join group');
    return res.data;
  },

  leaveGroup: async (groupId) => {
    const res = await apiClient.post(`${API_URL}/${groupId}/leave`);
    if (res.status !== 204) throw new Error('Failed to leave group');
    return groupId;
  },

  approveMember: async (groupId, userId) => {
    const res = await apiClient.post(`${API_URL}/${groupId}/members/${userId}/approve`);
    if (res.status !== 200) throw new Error('Failed to approve member');
    return res.data;
  },

  rejectMember: async (groupId, userId) => {
    const res = await apiClient.post(`${API_URL}/${groupId}/members/${userId}/reject`);
    if (res.status !== 200) throw new Error('Failed to reject member');
    return res.data;
  },

  fetchMyGroupInvites: async () => {
    const res = await apiClient.get(`${API_URL}/invites/mine`);
    if (res.status !== 200) throw new Error('Failed to fetch pending invites');
    return res.data;
  },

  acceptGroupInvite: async (groupId) => {
    const res = await apiClient.post(`${API_URL}/${groupId}/invites/accept`);
    if (res.status !== 200) throw new Error('Failed to accept invite');
    return res.data;
  },

  declineGroupInvite: async (groupId) => {
    const res = await apiClient.post(`${API_URL}/${groupId}/invites/decline`);
    if (res.status !== 200) throw new Error('Failed to decline invite');
    return res.data;
  },

  updateMemberRole: async (groupId, userId, newRole) => {
    const res = await apiClient.post(`${API_URL}/${groupId}/members/${userId}/role/${newRole}`);
    if (res.status !== 200) throw new Error('Failed to update member role');
    return res.data;
  },

  transferOwnership: async (groupId, newOwnerUserId) => {
    const res = await apiClient.post(`${API_URL}/${groupId}/transfer-ownership/${newOwnerUserId}`);
    if (res.status !== 204) throw new Error('Failed to transfer ownership');
    return { groupId, newOwnerUserId };
  },

  muteMember: async (groupId, userId, { reason, until } = {}) => {
    const res = await apiClient.post(`${API_URL}/${groupId}/members/${userId}/mute`, { reason, until });
    if (res.status !== 200) throw new Error('Failed to mute member');
    return res.data;
  },

  unmuteMember: async (groupId, userId) => {
    const res = await apiClient.post(`${API_URL}/${groupId}/members/${userId}/unmute`);
    if (res.status !== 200) throw new Error('Failed to unmute member');
    return res.data;
  },

  // Group Messages
  createGroupMessage: async (groupId, messageData) => {
    const res = await apiClient.post(`${API_URL}/${groupId}/messages`, messageData);
    if (res.status !== 201) throw new Error('Failed to create message');
    return res.data;
  },

  fetchGroupMessages: async (groupId, skip = 0, limit = 10) => {
    const res = await apiClient.get(`${API_URL}/${groupId}/messages`, {
      params: { skip, limit },
    });
    if (res.status !== 200) throw new Error('Failed to fetch messages');
    return res.data;
  },

  updateGroupMessage: async (messageId, content) => {
    const res = await apiClient.put(`${API_URL}/messages/${messageId}`, { content });
    if (res.status !== 200) throw new Error('Failed to update message');
    return res.data;
  },

  deleteGroupMessage: async (messageId) => {
    const res = await apiClient.delete(`${API_URL}/messages/${messageId}`);
    if (res.status !== 204) throw new Error('Failed to delete message');
    return messageId;
  },

  // Group Display Picture
  fetchGroupDisplayPicture: async (groupId) => {
    const res = await apiClient.get(`${API_URL}/${groupId}/picture`);
    if (res.status !== 200) {
      if (res.status === 404) return null; // No picture set
      throw new Error('Failed to fetch display picture');
    }
    return res.data;
  },

  upsertGroupDisplayPicture: async (groupId, pictureUrl) => {
    const res = await apiClient.post(`${API_URL}/${groupId}/picture`, { picture_url: pictureUrl });
    if (res.status !== 200 && res.status !== 201) throw new Error('Failed to upsert display picture');
    return res.data;
  },

  deleteGroupDisplayPicture: async (groupId) => {
    const res = await apiClient.delete(`${API_URL}/${groupId}/picture`);
    if (res.status !== 204) throw new Error('Failed to delete display picture');
    return groupId;
  },

  updateGroupBackgroundTheme: async (groupId, backgroundTheme) => {
    const res = await apiClient.patch(`${API_URL}/${groupId}/background-theme`, { background_theme: backgroundTheme });
    if (res.status !== 200) throw new Error('Failed to update background theme');
    return res.data;
  },

  // Search
  searchUsers: async (query, skip = 0, limit = 5) => {
    const res = await apiClient.get(`${API_URL}/search-users`, {
      params: { query, skip, limit }
    });
    if (res.status !== 200) throw new Error('Failed to search users');
    return res.data;
  },

  searchGroupsByStock: async (ticker, sector, industry, skip = 0, limit = 10) => {
    const params = { skip, limit };
    if (ticker) params.ticker = ticker;
    if (sector) params.sector = sector;
    if (industry) params.industry = industry;

    const res = await apiClient.get(`${API_URL}/search-by-stock`, { params });
    if (res.status !== 200) throw new Error('Failed to search groups by stock');
    return res.data;
  },

  // Analytics
  getGroupAnalytics: async (groupId) => {
    const res = await apiClient.get(`${API_URL}/${groupId}/analytics`);
    if (res.status !== 200) throw new Error('Failed to fetch group analytics');
    return res.data;
  }
};

export default GroupAPI;